import React from 'react';
import { Link } from 'react-router-dom';
import { Bell, ExternalLink, ChevronDown, Sparkles, MessageSquare, Wrench, Clock, ArrowRight } from 'lucide-react';
import Sidebar from './Sidebar';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';

import { ContactRequest, ServiceRequest } from '../services/api';

interface Notification {
  type: 'contact' | 'service';
  id: string; // Changed from number to string to match API responses
  created_at: string;
  status: string;
  name?: string;
  full_name?: string;
  email?: string;
  message?: string;
  service_name?: string;
}

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);

      const fetchWithResilience = async <T,>(fetcher: () => Promise<T[]>): Promise<T[]> => {
        try {
          return await fetcher();
        } catch (error) {
          console.warn('Notification fetch failed:', error);
          return [];
        }
      };

      const [contacts, services] = await Promise.all([
        fetchWithResilience(() => apiService.getContactRequests()),
        fetchWithResilience(() => apiService.getServiceRequests())
      ]);

      try {
        // Combine and sort by date
        const combined: Notification[] = [
          ...(contacts || []).map((c: ContactRequest) => ({ ...c, type: 'contact' as const })),
          ...(services || []).map((s: ServiceRequest) => ({ ...s, type: 'service' as const }))
        ].sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
          .slice(0, 5); // Only show top 5

        setNotifications(combined);
      } catch (e) {
        console.error("Error processing notifications", e);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();

    // Click away to close
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const pendingCount = notifications.filter(n =>
    (n.status || '').toString().toLowerCase() === 'new' ||
    (n.status || '').toString().toLowerCase() === 'pending'
  ).length;

  return (
    <div className="flex h-screen bg-[#F8F9FA] overflow-hidden">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {/* Wrapper: Becomes the scroll container for the dashboard content area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-y-auto relative z-0 h-screen">
        <header className="flex items-center justify-between bg-white/80 backdrop-blur-md border-b border-gray-100 px-4 md:px-8 py-4 sticky top-0 z-40 w-full">
          <div className="flex items-center gap-4 flex-1">
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden p-2 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-xl transition-all"
            >
              <div className="space-y-1.5">
                <div className="w-6 h-0.5 bg-current"></div>
                <div className="w-6 h-0.5 bg-current"></div>
                <div className="w-6 h-0.5 bg-current"></div>
              </div>
            </button>

            {/* Left side: Search bar (Aesthetic & Functional) */}

          </div>

          {/* Right side: Actions & Profile */}
          <div className="flex items-center gap-2 md:gap-6">
            {/* View Site Link */}
            <a
              href={process.env.REACT_APP_PUBLIC_SITE_URL || 'https://bdelectric.com.np'}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 px-4 py-2 text-xs font-bold text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-xl transition-all border border-transparent hover:border-orange-100"
            >
              <ExternalLink className="h-4 w-4" />
              <span className="hidden lg:inline">View Website</span>
            </a>

            {/* Notifications */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className={`relative p-2.5 rounded-xl transition-all group ${showNotifications ? 'bg-orange-600 text-white shadow-lg' : 'text-gray-400 hover:text-orange-600 hover:bg-orange-50'}`}
              >
                <Bell className="h-5 w-5" />
                {pendingCount > 0 && (
                  <span className={`absolute top-2 right-2 w-2 h-2 rounded-full border-2 ${showNotifications ? 'bg-white border-orange-600' : 'bg-orange-600 border-white'} group-hover:animate-pulse`}></span>
                )}
              </button>

              {/* Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-3 w-80 bg-white rounded-[2rem] shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-50">
                  <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                    <h3 className="font-bold text-gray-900">Recent Activity</h3>
                    {pendingCount > 0 && (
                      <span className="px-2 py-0.5 bg-orange-100 text-orange-600 text-[10px] font-black rounded-full uppercase tracking-tighter">
                        {pendingCount} New
                      </span>
                    )}
                  </div>

                  <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                    {loading ? (
                      <div className="p-10 flex flex-col items-center justify-center gap-3">
                        <div className="h-6 w-6 border-2 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-xs text-gray-400 font-bold">Synchronizing...</p>
                      </div>
                    ) : notifications.length > 0 ? (
                      <div className="divide-y divide-gray-50">
                        {notifications.map((n, i) => (
                          <Link
                            key={i}
                            to={n.type === 'contact' ? '/contact-requests' : '/service-requests'}
                            onClick={() => setShowNotifications(false)}
                            className="flex items-start gap-4 p-5 hover:bg-orange-50/50 transition-colors group"
                          >
                            <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${n.type === 'contact' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                              }`}>
                              {n.type === 'contact' ? <MessageSquare className="h-5 w-5" /> : <Wrench className="h-5 w-5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-gray-900 truncate">
                                {n.full_name || n.name || n.email || 'New Request'}
                              </p>
                              <p className="text-[10px] text-gray-500 line-clamp-1 mt-0.5">
                                {n.message || n.service_name || 'Checked in for more details.'}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <Clock className="h-3 w-3 text-gray-300" />
                                <span className="text-[10px] font-bold text-gray-400">
                                  {new Date(n.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            <div className="self-center">
                              <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-orange-600 transform group-hover:translate-x-1 transition-all" />
                            </div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="p-10 text-center">
                        <p className="text-sm text-gray-400 font-medium italic">No new activity to show.</p>
                      </div>
                    )}
                  </div>

                  <Link
                    to="/dashboard"
                    onClick={() => setShowNotifications(false)}
                    className="block w-full py-4 text-center text-xs font-black text-gray-400 uppercase tracking-[0.2em] bg-gray-50/50 hover:bg-gray-100 hover:text-orange-600 transition-all border-t border-gray-100"
                  >
                    View All Activity
                  </Link>
                </div>
              )}
            </div>

            <div className="h-8 w-px bg-gray-100 mx-2 hidden md:block"></div>

            {/* Profile Section */}
            <Link
              to="/profile"
              className="flex items-center gap-3 p-1 rounded-2xl hover:bg-gray-50 transition-all group border border-transparent hover:border-gray-100 bg-white"
            >
              <div className="relative">
                <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-gradient-to-tr from-orange-600 to-orange-400 flex items-center justify-center text-sm font-black text-white shadow-lg shadow-orange-100 overflow-hidden group-hover:rotate-3 transition-transform">
                  {user?.profile_image ? (
                    <img src={getImageUrl(user.profile_image) || undefined} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    user?.first_name?.[0] || user?.username?.[0]
                  )}
                </div>
                <div className="absolute -bottom-1 -right-1 w-3 h-3 md:w-4 md:h-4 bg-green-500 border-2 border-white rounded-full"></div>
              </div>

              <div className="hidden sm:block text-left mr-2">
                <div className="text-sm font-black text-gray-900 leading-tight flex items-center gap-1 group-hover:text-orange-600 transition-colors">
                  {user?.first_name} {user?.last_name}
                  <ChevronDown className="h-3 w-3 text-gray-400 group-hover:rotate-180 transition-transform" />
                </div>
                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest flex items-center gap-1">
                  <Sparkles className="h-2 w-2 text-orange-400" />
                  {user?.role?.replace('_', ' ')}
                </div>
              </div>
            </Link>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8 lg:p-10">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;